import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type {
  JourneyGraph,
  ScreenManifest,
  ScreenSurface,
} from '@/pages/screen-flows/screen-flow-types';

export type ScreenFlowStatus = 'draft' | 'published';

export type ScreenFlow = {
  id: string;
  surface: ScreenSurface;
  locationId: string | null;
  name: string;
  status: ScreenFlowStatus;
  version: number;
  manifestVersion: string;
  graph: JourneyGraph;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ScreenFlowListResponse = {
  flows: ScreenFlow[];
  page: number;
  pageSize: number;
  total: number;
};

const LIST_KEY = ['screen-flows'] as const;
const ONE_KEY = (id: string) => ['screen-flows', id] as const;
const MANIFEST_KEY = (surface: string) => ['screen-flows', 'manifest', surface] as const;

export function useScreenFlows() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async (): Promise<ScreenFlowListResponse> => {
      const res = await apiClient.get<ScreenFlowListResponse>('/screen-flows', {
        params: { pageSize: 200 },
      });
      return res.data;
    },
  });
}

export function useScreenFlow(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['screen-flows', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<ScreenFlow> => {
      const res = await apiClient.get<ScreenFlow>(`/screen-flows/${id}`);
      return res.data;
    },
  });
}

export function useScreenManifest(surface: ScreenSurface | undefined) {
  return useQuery({
    queryKey:
      surface === undefined ? ['screen-flows', 'manifest', '__noop'] : MANIFEST_KEY(surface),
    enabled: surface !== undefined,
    staleTime: 10 * 60 * 1000, // manifest is vendored; rarely changes
    queryFn: async (): Promise<ScreenManifest> => {
      const res = await apiClient.get<ScreenManifest>(`/screen-flows/manifests/${surface}`);
      return res.data;
    },
  });
}

export function useCreateScreenFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      surface: ScreenSurface;
      name: string;
      locationId?: string | null;
    }): Promise<ScreenFlow> => {
      const res = await apiClient.post<ScreenFlow>('/screen-flows', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useSaveScreenFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      expectedVersion: number;
      name?: string;
      graph: JourneyGraph;
    }): Promise<ScreenFlow> => {
      const res = await apiClient.put<ScreenFlow>(`/screen-flows/${vars.id}`, {
        expectedVersion: vars.expectedVersion,
        name: vars.name,
        graph: vars.graph,
      });
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(data.id) });
    },
  });
}

export function usePublishScreenFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<ScreenFlow> => {
      const res = await apiClient.post<ScreenFlow>(`/screen-flows/${id}/publish`, {});
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(data.id) });
    },
  });
}

export function useDeleteScreenFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/screen-flows/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}
